// ✅ src/pages/safety/SafetyView.jsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { saveSafetyDoc, getSafetyDocDetail } from "../../api/safety";
import { softDeleteDoc } from "../../api/papers";

// 공통 FormViewManager
import FormViewManager from "../../components/common/form/FormViewManager";

// --- 뷰 & 에디터 ---
import ReportView from "../../components/safety/view/ReportView";
import ChecklistView from "../../components/safety/view/ChecklistView";
import RiskcheckView from "../../components/safety/view/RiskcheckView";
import IncidentView from "../../components/safety/view/IncidentView";
import SCARView from "../../components/safety/view/SCARView";

import PlanEditor from "../../components/safety/editor/ReportEditor";
import ChecklistEditor from "../../components/safety/editor/ChecklistEditor";
import RiskcheckEditor from "../../components/safety/editor/RiskcheckEditor";
import IncidentEditor from "../../components/safety/editor/IncidentEditor";
import SCAREditor from "../../components/safety/editor/SCAREditor";
import { viewApprovalLine } from "../../api/approval";

// ------------------------------------------------------------------
export default function SafetyViewer() {
  const nav = useNavigate();
  const { doc_type, dns } = useParams();
  const projectId = localStorage.getItem("projectId");
  const currentUser = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return { id: Number(parsed.id), name: parsed.name || "알 수 없음" };
    } catch {
      return {};
    }
  }, []);

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [approvalLines, setApprovalLines] = useState([]);

  useEffect(() => {
    if (!dns) return;

    (async () => {
      try {
        const linesRes = await viewApprovalLine(dns);
        setApprovalLines(linesRes.data?.approval_lines || []);
      } catch (e) {
        console.error("결재선 로드 실패", e);
      }
    })();
  }, [dns]);


  const normalizeTitle = (t) => {
    const v = String(t || "").trim();
    return v && v !== "(제목 없음)" ? v : "";
  };

  const buildRiskcheckTitle = (meta = {}) => {
    const proc = String(meta.process || "").trim();
    const loc = String(meta.location || "").trim();
    const d =
      String(meta.doc_date || "").slice(0, 10) ||
      new Date().toISOString().slice(0, 10);
    if (proc && loc) return `[위험성 평가] ${proc} - ${loc} (${d})`;
    if (proc) return `[위험성 평가] ${proc} (${d})`;
    if (loc) return `[위험성 평가] ${loc} (${d})`;
    return `[위험성 평가] ${d}`;
  };

  const buildIncidentTitle = (meta = {}) => {
    const t = String(meta.incident_type || "").trim();
    const l = String(meta.location || "").trim();
    const d =
      String(meta.incident_date || meta.doc_date || "").slice(0, 10) ||
      new Date().toISOString().slice(0, 10);
    if (t && l) return `[사고] ${t} - ${l} (${d})`;
    if (t) return `[사고] ${t} (${d})`;
    if (l) return `[사고] ${l} (${d})`;
    return `[사고] ${d}`;
  };

  const isOwner = Number(doc?.created_by_id) === Number(currentUser?.id);

  useEffect(() => {
    if (!doc || !doc.meta) return;

    const refs = doc.meta.reference_users;
    const me = Number(currentUser?.id);

    // 1) 전체 열람 가능 ALL
    if (refs === "ALL") return;

    // 2) 배열인데 내가 포함되지 않은 경우
    // if (Array.isArray(refs) && !refs.includes(me)) {
    //   alert("접근 권한이 없습니다.");
    //   nav(-1); // 뒤로가기
    // }
  }, [doc, currentUser, nav]);

  // === 문서 로드 ===
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const data = await getSafetyDocDetail(dns);
        if (!alive) return;
        setDoc(data);
      } catch (e) {
        setError(e?.message || "문서를 불러오지 못했습니다.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [dns]);

  // === 저장 ===
  const handleSave = async () => {
    if (!draft) return;
    try {
      setSaving(true);
      const mergedMeta = draft?.meta || doc?.meta || {};
      const safeTitle =
        normalizeTitle(draft?.title) ||
        normalizeTitle(doc?.title) ||
        (doc_type === "riskcheck"
          ? buildRiskcheckTitle(mergedMeta)
          : doc_type === "incident"
            ? buildIncidentTitle(mergedMeta)
            : "");

      const payload = {
        dns,
        project: projectId,
        title: safeTitle,
        doc_type: doc?.doc_type || doc_type,
        status: draft?.meta?.status || doc?.status || "draft",
        meta: draft?.meta || draft || {},
      };
      await saveSafetyDoc(payload);
      alert("저장 완료");
      setIsEditing(false);
      const refreshed = await getSafetyDocDetail(dns);
      setDoc(refreshed);
      setDraft(null);
    } catch (e) {
      console.error(e);
      alert("저장 실패");
    } finally {
      setSaving(false);
    }
  };

  // === 삭제 ===
  const handleDelete = async () => {
    if (!window.confirm("이 문서를 삭제하시겠습니까?")) return;
    try {
      await softDeleteDoc(dns, true);
      alert("문서가 삭제되었습니다.");
      nav(`/project/${projectId}/safety/board/${doc_type}`, { replace: true });
    } catch (e) {
      alert("삭제 실패");
    }
  };

  // === 매핑 ===
  const VIEW_BY_TYPE = {
    report: ReportView,
    check: ChecklistView,
    scar: SCARView,
    incident: IncidentView,
    riskcheck: RiskcheckView,
  };
  const EDITOR_BY_TYPE = {
    report: PlanEditor,
    check: ChecklistEditor,
    scar: SCAREditor,
    incident: IncidentEditor,
    riskcheck: RiskcheckEditor,
  };

  const ViewComp = VIEW_BY_TYPE[doc_type];
  const EditComp = EDITOR_BY_TYPE[doc_type];

  if (loading)
    return (
      <div className="p-6 text-gray-400 text-center">문서를 불러오는 중...</div>
    );
  if (error)
    return <div className="p-6 text-rose-600 text-center">오류: {error}</div>;
  if (!doc)
    return (
      <div className="p-6 text-gray-400 text-center">문서가 없습니다.</div>
    );

  // === formType 명시용 ===
  const FORM_LABEL = {
    report: "안전관리 계획서",
    plan: "안전 점검 일정",
    result: "안전 점검 결과",
    check: "안전 점검 문서",
    scar: "안전 시정 조치 요구서",
    incident: "안전 사고 보고서",
    riskcheck: "위험성 평가서",
  };

  const FORM_LABEL_ENG = {
    report: "Safety Management Plan",
    plan: "Safety Inspection Schedule",
    result: "Safety Inspection Result",
    check: "Safety Inspection Document",
    scar: "Security and Corrective Action Report",
    incident: "Safety Incident Report",
    riskcheck: "Risk Assessment Report",
  };
  const docName = `${FORM_LABEL[doc_type] || "안전 문서"}`;

  const docEng =
    FORM_LABEL_ENG[doc_type] || doc_type.toUpperCase();

  const actionCount = doc?.meta?.action_histories?.length || 0;
  const approvalCount = approvalLines.length;

  const showApprovalPanel =
    doc?.status === "in_progress" &&
    approvalCount === actionCount + 1;

  console.log(actionCount, approvalCount, showApprovalPanel, "^^")


  // === 렌더링 ===
  return (
    <FormViewManager
      params={{
        project: projectId,
        dns,
        module: "safety",
        doc_type,
      }}
      doc_name={docName}
      doc_eng={docEng}
      app_action={showApprovalPanel}
    >
      {isEditing && EditComp ? (
        <EditComp
          initial={{
            ...doc.meta, // 메타 필드
            title: doc.title, //Changed: 제목 전달
            doc_date: doc.doc_date, //Changed: 작성일도 전달하면 더 정확
          }}
          onChange={setDraft}
          readOnly={false}
          currentUser={currentUser}
        />
      ) : (
        ViewComp && <ViewComp doc={doc} currentUser={currentUser} />
      )}

      {/* ✅ 문서 하단 조작 버튼 (FormViewManager 내부 PaperShell 하단에 표시) */}
      {isOwner && (
        <div className="no-print flex justify-end mt-6 gap-3">
          {!isEditing ? (
            <>
              <button
                onClick={() => {
                  if (doc_type === "scar" && doc?.meta?.result_reply) {
                    alert("결과가 등록된 문서는 수정할 수 없습니다.");
                    return; // 수정모드 진입 차단
                  }
                  setIsEditing(true);
                }}
                className="btn-edit"
              >
                수정
              </button>
              <button
                onClick={() => {
                  if (doc_type === "scar" && doc?.meta?.result_reply) {
                    alert("결과가 등록된 문서는 삭제할 수 없습니다.");
                    return;
                  }
                  handleDelete();
                }}
                className="btn-delete"
              >
                삭제
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn-save"
              >
                {saving ? "저장 중..." : "저장"}
              </button>
              <button
                onClick={() => setIsEditing(false)}
                className="btn-cancel"
              >
                취소
              </button>
            </>
          )}
        </div>
      )}
    </FormViewManager>
  );
}
